import React, { useEffect, useState } from "react";
import { Amplitude, LogOnMount, useAmplitude } from "react-amplitude-hooks";
import { Link } from "react-router-dom";
import styled from "@emotion/styled";
import { TravelContainerProps } from "components/Travel/interface";
import { SubscriptionDetailValue } from "components/Travel/state";
import {
  stringToDate,
  numberWithCommas,
  numberToTell,
  planTypeName,
  toTop,
  getVh
} from "components/Travel/function";
import { countryName } from "components/Travel/country";
import {
  Title,
  TitleBox,
  IconPerson,
  ButtonSmall,
  ButtonArrow,
  Section,
  Divider,
  HistoryContainer
} from "style/components/";
import GuaranteePopup from "components/Travel/Mobile/GuaranteePopup";
import SubscriptionFooter from "./footer";

const SubscriptionDetail: React.FunctionComponent<TravelContainerProps> = ({
  match
}) => {
  const [data, setData] = useState(SubscriptionDetailValue);
  const [isData, setIsData] = useState(false);
  const { logEvent } = useAmplitude();
  const [personType, setPlanContent] = useState({
    code: "",
    name: ""
  });
  const [isShowPopup, togglePopup] = useState(false);
  const onClickPlanButton = (code: string, name: any) => {
    setPlanContent({
      code,
      name
    });
    togglePopup(!isShowPopup);
  };
  useEffect(() => {
    toTop();
    getVh();
    if (!isData) {
      if (sessionStorage.getItem("subscription")) {
        const subscriptionList: string =
          sessionStorage.getItem("subscription") || "";
        let subscriptionData: any = [];
        if (subscriptionList) {
          setIsData(true);
          subscriptionData = JSON.parse(subscriptionList);
          setData(subscriptionData[match.params.id]);
        }
      } else {
        window.location.replace("/travel/mrt/subscription");
      }
    }
  }, [data, match.params.id, isData]);

  return (
    <Amplitude>
      <LogOnMount eventType="history_detail" />
      <HistoryContainer>
        <TitleBox>
          <Title>가입보험 상세보기</Title>
        </TitleBox>
        {isData && (
          <>
            <ConfirmBox first={true}>
              <Definition className="first">
                <dt>보험명</dt>
                <dd>Chubb 해외여행보험</dd>
              </Definition>
              <Definition>
                <dt>여행국가</dt>
                <dd>{countryName(data.travelInfo.country)}</dd>
              </Definition>
              <Definition>
                <dt>보험기간</dt>
                <dd>
                  <span className="number">
                    {stringToDate(data.policyStartDate)}
                  </span>{" "}
                  (<span className="number">{data.policyStartDateTime}</span>시)
                  <span> ~ </span>
                  <span className="number">
                    {stringToDate(data.policyEndDate)}
                  </span>{" "}
                  (<span className="number">{data.policyEndDateTime}</span>시)
                </dd>
              </Definition>
              <Definition>
                <dt>가입일시</dt>
                <dd>{stringToDate(data.insuranceDate)}</dd>
              </Definition>
              <Definition>
                <dt>가입인원</dt>
                <dd>
                  <span className="number">
                    {data.travelInfo.companionInfoList.length}
                  </span>
                  명
                </dd>
              </Definition>
              <Definition>
                <dt>보험료</dt>
                <dd>
                  <span className="number">
                    {numberWithCommas(data.totalAmount)}
                  </span>
                  원<TotalPriceInfo>(일시납)</TotalPriceInfo>
                </dd>
              </Definition>
            </ConfirmBox>
            <ConfirmBox>
              <Definition className="first">
                <dt>대표 피보험자</dt>
                <dd>{data.insuredInfo.name}</dd>
              </Definition>
              <Definition>
                <dt>휴대전화번호</dt>
                <dd className="number">
                  {numberToTell(data.insuredInfo.telMobile)}
                </dd>
              </Definition>
              <Definition>
                <dt>이메일</dt>
                <dd className="number">{data.insuredInfo.email}</dd>
              </Definition>
            </ConfirmBox>

            <ConfirmBox>
              {data.travelInfo.companionInfoList.map(
                (item: any, index: number) => (
                  <PersonWrap key={index} index={index}>
                    <p>
                      <IconPerson />
                      {item.name}: {planTypeName(item.planCode)}
                    </p>
                    <ButtonSmall
                      type="button"
                      onClick={() => {
                        logEvent("history_info_button", {
                          plan_type: planTypeName(item.planCode)
                        });
                        onClickPlanButton(
                          item.planCode,
                          planTypeName(item.planCode)
                        );
                      }}
                    >
                      보장내용
                    </ButtonSmall>
                  </PersonWrap>
                )
              )}
            </ConfirmBox>
            <Divider />
            <Section>
              <CertificateEngNotice>
                <span className="line">
                  영문 가입 증명서가 필요하다면 이메일로 성함, 휴대전화번호를
                </span>
                <span className="line">
                  입력하여 보내주세요. (영업일 기준 1~2일 내 발급됩니다.)
                </span>
                <span className="email">
                  <a href="mailto: help@bomappbiz.co.kr" className="underline">
                    help@bomappbiz.co.kr
                  </a>
                </span>
              </CertificateEngNotice>
            </Section>
            <Divider />
            <Section className="spaceBottom spaceTop">
              <Link
                to="/travel/mrt/subscription/list"
                onClick={() => logEvent("history_list_button")}
              >
                <ButtonArrow>가입내역 가기</ButtonArrow>
              </Link>
            </Section>
            {isShowPopup && (
              <GuaranteePopup
                togglePopup={() => togglePopup(!isShowPopup)}
                contents={personType.code}
                title={personType.name}
              />
            )}
          </>
        )}
      </HistoryContainer>
      <SubscriptionFooter />
    </Amplitude>
  );
};
interface ConfirmBoxProps {
  first?: boolean;
}

const ConfirmBox = styled.div<ConfirmBoxProps>`
  padding: 30px 20px;
  border-top: 1px solid ${props => (props.first ? "#070e1a" : "#efefef")};

  &.last {
    padding-bottom: 50px;
  }
`;

const Definition = styled.dl`
  display: flex;
  align-items: center;
  margin-top: 16px;

  &.first {
    margin-top: 0;
  }

  > dt {
    width: 90px;
    font-weight: ${props => props.theme.weight.bolder};
  }

  .number {
    font-family: ${props => props.theme.fonts.number};
    font-weight: 300;
  }
`;

const TotalPriceInfo = styled.span`
  margin-left: 4px;
  color: ${props => props.theme.text.colorFont};
  font-size: ${props => props.theme.text.sizeFont};
`;

interface PersonWrapProps {
  index: number;
}

const PersonWrap = styled.div<PersonWrapProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: ${props => (props.index === 0 ? "0" : "14px")};

  > p {
    display: flex;
    font-weight: ${props => props.theme.weight.bolder};

    > span {
      margin-right: 13px;
    }
  }
`;

const CertificateEngNotice = styled.p`
  padding-top: 25px;
  padding-bottom: 25px;
  color: ${props => props.theme.colors.textSub};

  .line {
    display: block;

    @media only screen and (max-width: 370px) {
      display: inline;
    }
  }

  .email {
    display: block;
    margin-top: 10px;
    color: ${props => props.theme.colors.text2};
  }
`;

export default SubscriptionDetail;
